/* eslint-disable no-restricted-syntax */
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import * as ArchiveActions from 'archive/ArchiveActions';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import * as ArchiveFilterUtil from 'archive/ArchiveFilterUtil';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import * as BenchlingUtil from 'BenchlingUtil';
import {setWindowTitle} from 'BenchlingUtil';
import {RegistryPreferences} from 'constants/RegistryConstants';
import SchemaFieldSelectorManager from 'models/managers/SchemaFieldSelectorManager';
import preferences from 'Preferences';
import * as RegistryUtil from 'registry/RegistryUtil';
import SchemaFieldSelectorActions from 'registry/schema-field-selector/SchemaFieldSelectorActions';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import EditableItemTable from 'ui-library/EditableItemTable';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';
import * as modals from 'util/Modals';
import strUtil from 'util/strUtil';

const EMPTY_SEARCH_QUERY = '';

function mapSchemaFieldSelectorStoreToProps(
  schemaFieldSelectorStore: typeof SchemaFieldSelectorStore,
  {registryId}: RegistrySelectorsSettingsPageProps
): ConnectedProps {
  return {
    selectors: schemaFieldSelectorStore.getOrderedSelectorsForRegistry(registryId, {includeArchived: true}),
  };
}

export interface RegistrySelectorsSettingsPageProps {
  ownerUrlId: string;
  registryId: string;
}

interface ConnectedProps {
  selectors: Array<SchemaFieldSelector> | null;
}

interface QueriedConnectedProps {
  isWritable: boolean;
}

interface State {
  selectedArchivePurposes: Array<string>;
  searchQuery: string;
}

export class RegistrySelectorsSettingsPage extends React.Component<
  RegistrySelectorsSettingsPageProps & ConnectedProps & QueriedConnectedProps,
  State
> {
  override state: State = {
    selectedArchivePurposes: [NOT_ARCHIVED_PURPOSE_STRING],
    searchQuery: EMPTY_SEARCH_QUERY,
  };

  override componentDidMount(): void {
    SchemaFieldSelectorManager.fetchForRegistry(this.props.registryId);
  }

  override componentDidUpdate(): void {
    SchemaFieldSelectorManager.fetchForRegistry(this.props.registryId);
  }

  private getShownSelectors = (): Array<SchemaFieldSelector> => {
    let filteredSelectors =
      ArchiveFilterUtil.filterItemsToPurposes(this.props.selectors, this.state.selectedArchivePurposes) ?? [];
    if (this.state.searchQuery !== EMPTY_SEARCH_QUERY) {
      filteredSelectors = filteredSelectors.filter((selector) =>
        strUtil.caseInsensitiveContains(selector.name, this.state.searchQuery)
      );
    }
    return filteredSelectors;
  };

  private handleChangeSelectedArchivePurposes = (selectedArchivePurposes: Array<string>): void => {
    this.setState({selectedArchivePurposes});
  };

  private handleCreate = (): void => {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getCreateSchemaFieldSelectorUrl(this.props.ownerUrlId, this.props.registryId)
    );
  };

  private getEditUrl = (index: number): string => {
    const selector = this.getShownSelectors()[index];
    return RegistryUtil.getSchemaFieldSelectorSettingsUrl(
      this.props.ownerUrlId,
      this.props.registryId,
      selector.id
    );
  };

  private handleArchive = (index: number): void => {
    const selector = this.getShownSelectors()[index];
    ArchiveModal.open({items: [selector], onArchive: ArchiveActions.bulkArchiveSchemaFieldSelectors});
  };

  private handleUnarchive = (index: number): void => {
    const selector = this.getShownSelectors()[index];
    ArchiveActions.bulkUnarchiveSchemaFieldSelectors([selector.id]);
  };

  private handleDelete = (index: number): void => {
    const selector = this.getShownSelectors()[index];
    modals
      .confirm(`Delete "${selector.name}"`, 'Are you sure you want to delete this dropdown?', 'danger')
      .done(SchemaFieldSelectorActions.delete.bind(null, selector.id));
  };

  override render(): JSX.Element {
    setWindowTitle('Dropdowns - Registry Settings');

    const columnHeaders = [
      {name: 'Name', width: 4},
      {name: '# of Options', width: 2},
      {name: 'Options', width: 6},
    ];
    let rows;
    if (this.getShownSelectors()) {
      rows = this.getShownSelectors().map((selector, i) => ({
        id: selector.id,
        itemIndex: i,
        isArchived: isArchived(selector),
        values: [
          <span className="u-small-spacing-left">
            {selector.name}
            <ArchiveRecordLabel item={selector} />
          </span>,
          selector.schemaFieldSelectorOptions.length,
          _.pluck(selector.schemaFieldSelectorOptions, 'name').join(', '),
        ],
      }));
    }
    const isLoading = this.props.selectors == null;
    return (
      <div
        className="settingsPage"
        {...getTestProps({component: 'RegistrySelectorsSettingsPage', element: 'root'})}
      >
        <div className="settingsPage-contentWrapper">
          <EditableItemTable
            tableName="Dropdowns"
            columnHeaders={columnHeaders}
            emptyMessage="This registry has no dropdowns"
            isLoading={isLoading}
            onArchive={this.handleArchive}
            onCreate={this.handleCreate}
            onDelete={this.handleDelete}
            allowDeleteAfterArchive={preferences.get(RegistryPreferences.ENABLE_DROPDOWN_DELETE)}
            getEditUrl={this.getEditUrl}
            onUnarchive={this.handleUnarchive}
            rows={rows}
            leftSubheaderNode={
              isLoading ? null : (
                <div className="u-flex-display">
                  <PlainSearchInput
                    className={css(styles.searchInput)}
                    borderRadius={BorderRadius.LARGE}
                    placeholder="Search"
                    value={this.state.searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      this.setState({
                        searchQuery: e.target.value,
                      });
                    }}
                    onEscape={() => {
                      this.setState({
                        searchQuery: EMPTY_SEARCH_QUERY,
                      });
                    }}
                  />
                  <ArchivePurposeDropdown
                    itemIds={this.props.selectors.map((selector) => selector.id)}
                    onChangePurposes={this.handleChangeSelectedArchivePurposes}
                    selectedPurposes={this.state.selectedArchivePurposes}
                  />
                </div>
              )
            }
            writable={this.props.isWritable}
          />
        </div>
      </div>
    );
  }
}

function QueryingRegistrySelectorsSettingsPage(
  props: RegistrySelectorsSettingsPageProps & ConnectedProps
): JSX.Element {
  const isWritable = RegistryUtil.useIsRegistryWritableQuery(props.registryId);
  return <RegistrySelectorsSettingsPage {...props} isWritable={isWritable} />;
}

const styles = StyleSheet.create({
  searchInput: {
    marginRight: 10,
    width: 200,
  },
});

export default SchemaFieldSelectorStore.connect(mapSchemaFieldSelectorStoreToProps)(
  QueryingRegistrySelectorsSettingsPage
);
